<script lang="ts">
  /**
   * その場で実行できる例（lesson-format.md §7.1）。
   * 原稿のコードは CodeBlock で見せ、「書き換えて試す」で CodeEditor に替える。「元に戻す」で原稿に戻す。
   * sandboxd で実行できないときは、原稿のままなら「Swift 6.2 で記録した出力」を出す。
   */
  import { onDestroy } from 'svelte';
  import CodeBlock from '../CodeBlock.svelte';
  import CodeEditor from '../CodeEditor.svelte';
  import OutputPanel from '../OutputPanel.svelte';
  import { SUBMIT_CHORD_LABEL } from '../keyboard';
  import { RunState } from '../cards/run-state.svelte';
  import RunStatus from '../cards/RunStatus.svelte';
  import RunResult from './RunResult.svelte';
  import { runLessonCode, type RunOutcomeView } from './run-lesson';
  import { outcomeLabel } from './experiment';
  import type { Expect } from './types';

  interface Props {
    code: string;
    stdin?: string | undefined;
    expect: Expect;
    /** 「実行 2」のような名前（aria-label に使う） */
    label: string;
    /** 最初から書き換えられる状態で出す（実験の後の「書き換えて試す」） */
    editing?: boolean;
  }

  let { code: original, stdin: originalStdin, expect, label, editing = false }: Props = $props();

  const uid = $props.id();
  // svelte-ignore state_referenced_locally
  let code = $state(original);
  // svelte-ignore state_referenced_locally
  let stdin = $state(originalStdin ?? '');
  // svelte-ignore state_referenced_locally
  let isEditing = $state(editing);
  let result = $state<RunOutcomeView | null>(null);
  /** 失敗したときに、どの入力で実行したか */
  let failedPristine = $state(false);

  const run = new RunState();
  onDestroy(() => run.dispose());

  const pristine = $derived(code === original && stdin === (originalStdin ?? ''));
  const hasStdin = $derived(originalStdin !== undefined || isEditing);

  async function execute() {
    if (run.running || code.trim() === '') return;
    result = null;
    failedPristine = pristine;
    const snapshot = { code, stdin };
    const res = await run.start((hooks) => runLessonCode(snapshot.code, snapshot.stdin, hooks));
    if (res) result = res;
  }

  function reset() {
    code = original;
    stdin = originalStdin ?? '';
    isEditing = false;
    result = null;
  }
</script>

<section class="run-block" aria-label={label} data-testid="run-block">
  {#if isEditing}
    <CodeEditor
      bind:value={code}
      id="{uid}-editor"
      readonly={run.running}
      minLines={Math.min(8, Math.max(3, original.split('\n').length))}
      onsubmit={() => void execute()}
    />
  {:else}
    <CodeBlock code={original.replace(/\n$/, '')} label="{label} のコード" />
  {/if}

  {#if hasStdin}
    <div class="stdin">
      <label class="sub" for="{uid}-stdin">標準入力</label>
      <textarea
        id="{uid}-stdin"
        class="stdin-input"
        rows={Math.min(5, Math.max(1, stdin.replace(/\n$/, '').split('\n').length))}
        spellcheck="false"
        autocapitalize="off"
        autocomplete="off"
        data-testid="run-stdin"
        bind:value={stdin}></textarea>
    </div>
  {/if}

  <div class="actions">
    <button
      class="btn btn-primary btn-sm"
      type="button"
      data-testid="run-execute"
      disabled={run.running || code.trim() === ''}
      aria-busy={run.running ? 'true' : undefined}
      onclick={() => void execute()}
      >{run.running ? '実行中' : '実行'}{#if isEditing}<kbd>{SUBMIT_CHORD_LABEL}</kbd>{/if}</button
    >
    {#if isEditing}
      <button class="btn btn-sm" type="button" data-testid="run-reset" onclick={reset}
        >元に戻す</button
      >
    {:else}
      <button
        class="btn btn-quiet btn-sm"
        type="button"
        data-testid="run-edit"
        onclick={() => (isEditing = true)}>書き換えて試す</button
      >
    {/if}
  </div>

  <RunStatus {run} busyLabel="実行中" onretry={() => void execute()} />

  {#if !run.running && run.error && failedPristine}
    <div class="recorded" data-testid="run-recorded">
      <h4 class="sub">Swift 6.2 で記録した出力</h4>
      {#if expect.outcome === 'ok'}
        <OutputPanel mode="plain" stdout={expect.expected} />
      {:else}
        <p>{outcomeLabel(expect.outcome)}になります。</p>
      {/if}
    </div>
  {/if}

  {#if result}
    <RunResult {result} />
  {/if}
</section>

<style>
  .run-block {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    min-width: 0;
    margin: var(--space-4) 0;
  }
  .stdin {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }
  .stdin-input {
    font-family: var(--font-mono);
    font-size: var(--fs-code);
  }
  .sub {
    font-size: var(--fs-sm);
    color: var(--text-soft);
    font-weight: var(--fw-semibold);
  }
  .actions {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex-wrap: wrap;
  }
  .actions kbd {
    margin-left: var(--space-2);
  }
  .recorded {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
</style>
